import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  GlobalConfiguration,
  PaginationConfiguration,
  ScreenSizeConfiguration,
} from "../../../utils/configurationProvider";
import { getAllCategories } from "../../../redux/features/sharedResources/sharedResources";
import {
  editFieldForceAccount,
  getAllFieldForceAccount,
  getSpecificFieldForceAccount,
} from "../../../redux/features/accounts/fieldForceAccount";
import UniversalRefreshToast from "../../_utils/UniversalRefreshToast";
import CommonToast from "../../_utils/CommonToast";
import ICONS from "../../../assets/Icons";
import PaginationComponent from "../../_utils/PaginationComponent";
import PrimaryStepper from "../../_utils/CommonSteppers/PrimaryStepper";
import CategoriesEdit from "../../_utils/AccountCreationComponents/CategoriesEdit";
import TextWithIcon from "../../_utils/CommonViewAllCard/CardSubComponent/TextWithIcon";
import IfscCodeMapper from "../../_utils/IfscLogoMapper";
import TickerAnimation from "../../_utils/TickerAnimation";
import { formatAccountNumber } from "../../_utils/FormatAccountNumber";
import CardContainer from "../../_utils/CommonViewAllCard/CardContainer";
import Loader from "../../Loader/Index";
import PrimaryButton from "../../_utils/CommonButtons/PrimaryButton";
import TopNavComponent from "../../_utils/CommonTopNav/TopNavComponent";
import "./index.css";

const FieldForceUnassignCategories = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchActionBtnRef = useRef(null);
  const accountListRef = useRef(null);

  const dropdownOptions = PaginationConfiguration.paginationOptions;
  const [currentStatus, setCurrentStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(
    GlobalConfiguration.lengthCheckOne,
  );

  const [showInnerSearchBar, setShowInnerSearchBar] = useState(false);
  const [active, setActive] = useState("");
  const [dataLoading, setDataLoading] = useState(false);
  const [inputData, setInputData] = useState([]);
  const [defaultCategories, setDefaultCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [accountInitialCategories, setAccountInitialCategories] = useState([]);
  const [preSelectedCategories, setPreSelectedCategories] = useState([]);
  const [fieldForceAccounts, setFieldForceAccounts] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [fieldForceAccountsLoading, setFieldForceAccountsLoading] =
    useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoadingSelectedAccount, setIsLoadingSelectedAccount] =
    useState(false);
  const [selectionRevision, setSelectionRevision] = useState(0);
  const [lastUpdateAttemptRevision, setLastUpdateAttemptRevision] =
    useState(null);
  const [fieldForceAccountSelected, setFieldForceAccountSelected] = useState(
    [],
  );
  const [searchUserText, setSearchUserText] = useState("");
  const [searchUserTextDebounce, setSearchUserTextDebounce] = useState("");

  const [activeStep, setActiveStep] = useState(
    GlobalConfiguration.lengthCheckZero,
  );
  const [checkmobile, setCheckmobile] = useState(false);
  const [pageSize, setPageSize] = useState(
    dropdownOptions[GlobalConfiguration.lengthCheckZero],
  );

  const categoriesArray = defaultCategories.map((category) => category.name);

  const handleSearch = (text) => {
    setSearchUserTextDebounce(text);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > ScreenSizeConfiguration.mobileScreen) {
        setActiveStep(GlobalConfiguration.lengthCheckZero);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    dispatch(getAllCategories());
  }, [dispatch]);

  const categoriesData = useSelector(
    (state) => state.sharedResources?.categoriesData?.categories,
  );

  useEffect(() => {
    setDefaultCategories(
      (categoriesData || []).filter((category) => category?.active === "Y"),
    );
  }, [categoriesData]);

  const services = useSelector((state) => state.refreshToken.services);

  useEffect(() => {
    if (active !== "Category") return;

    let isCurrent = true;
    setDataLoading(true);

    (async () => {
      const response = await dispatch(
        getAllCategories({ search_string: searchUserTextDebounce }),
      );
      const data = response?.payload?.categories?.map((itm) => ({
        id: itm.name,
        Category: itm.name,
        Description: itm?.description || "Not given",
      }));

      if (isCurrent) {
        setInputData(data);
        setDataLoading(false);
      }
    })();

    return () => {
      isCurrent = false;
    };
  }, [active, searchUserTextDebounce, dispatch]);

  const fieldForceEligibleAcct = (accounts) => {
    const account = accounts?.[GlobalConfiguration.lengthCheckZero];
    if (!account) {
      setFieldForceAccountSelected([]);
      setSelectedCategories([]);
      setAccountInitialCategories([]);
      return;
    }

    setFieldForceAccountSelected([account.id]);
    const accountCategories = Array.isArray(account.category_ids)
      ? account.category_ids
          .map((category) => category?.category_name)
          .filter(Boolean)
      : [];
    setSelectedCategories(accountCategories);
    setAccountInitialCategories(accountCategories);
  };

  const fetchAccountData = useCallback(
    async (page, searchUserText) => {
      const queryParams = {
        page_no: page,
        page_size: pageSize,
        approval_status:
          currentStatus && currentStatus !== "all"
            ? currentStatus.toLowerCase()
            : ["pending", "approved", "rejected"],
        unassigned_category: true,
        ...(searchUserText ? { search_string: searchUserText } : {}),
      };

      setFieldForceAccountsLoading(true);
      try {
        const response = await dispatch(getAllFieldForceAccount(queryParams));
        const payload = response?.payload;

        if (payload?.status === true) {
          setFieldForceAccounts(payload?.field_force_accounts || []);
          setPaginationData({
            ...payload,
            total_count: payload?.total_count ?? payload?.title?.records ?? 0,
          });
        } else {
          setFieldForceAccounts([]);
          setPaginationData({});
        }
      } catch {
        setFieldForceAccounts([]);
        setPaginationData({});
      } finally {
        setFieldForceAccountsLoading(false);
      }
    },
    [currentStatus, dispatch, pageSize],
  );

  useEffect(() => {
    fetchAccountData(currentPage, searchUserText);
  }, [currentPage, currentStatus, searchUserText, pageSize, fetchAccountData]);

  useEffect(() => {
    fieldForceEligibleAcct(fieldForceAccounts);
  }, [fieldForceAccounts]);

  useEffect(() => {
    if (
      Array.isArray(defaultCategories) &&
      accountInitialCategories.length > GlobalConfiguration.lengthCheckZero
    ) {
      const preSelectedCategories = defaultCategories.filter((category) =>
        accountInitialCategories.includes(category.name),
      );
      setPreSelectedCategories(preSelectedCategories);
    } else {
      setPreSelectedCategories([]);
    }
  }, [defaultCategories, accountInitialCategories]);

  const nonPreSelectedCategories = defaultCategories.filter(
    (category) =>
      !preSelectedCategories.some(
        (selectedCat) => selectedCat.id === category.id,
      ),
  );

  const sortedCategories = [
    ...preSelectedCategories,
    ...nonPreSelectedCategories,
  ];
  const selectedCategoryIds = defaultCategories
    .filter((category) =>
      selectedCategories.some(
        (selectedCategory) =>
          String(selectedCategory) === String(category.id) ||
          String(selectedCategory).trim().toLowerCase() ===
            category.name?.trim().toLowerCase(),
      ),
    )
    .map((category) => category.id);

  const handleCategorySelect = (categoryId) => {
    const category = defaultCategories.find((cat) => cat.id === categoryId);
    if (!category) return;

    setSelectionRevision((revision) => revision + 1);
    setSelectedCategories((prev) =>
      prev.includes(category.name)
        ? prev.filter((name) => name !== category.name)
        : [...prev, category.name],
    );
  };

  const handleClickAccountCard = async (id) => {
    if (
      id !== fieldForceAccountSelected?.[GlobalConfiguration.lengthCheckZero]
    ) {
      setSelectionRevision((revision) => revision + 1);
    }
    setFieldForceAccountSelected([id]);
    setIsLoadingSelectedAccount(true);

    try {
      const response = await dispatch(getSpecificFieldForceAccount({ id }));
      const account =
        response?.payload?.field_force_accounts?.[
          GlobalConfiguration.lengthCheckZero
        ];

      if (response?.payload?.status === true) {
        const selectedCategoriesArray = Array.isArray(account?.category_ids)
          ? account.category_ids
              .map((cat) => cat?.category_name)
              .filter(Boolean)
          : [];
        setSelectedCategories(selectedCategoriesArray);
        setAccountInitialCategories(selectedCategoriesArray);
      }
    } finally {
      setIsLoadingSelectedAccount(false);
    }
  };

  const isCurrentSelectionAlreadyAttempted =
    lastUpdateAttemptRevision === selectionRevision;

  const handleUpdateAccount = async (id) => {
    if (
      services?.includes("Admin") ||
      (services?.includes("FFVAccount") &&
        services?.includes("PLogin") &&
        services?.includes("FFUAccount"))
    ) {
      UniversalRefreshToast("unassignCate");
      if (
        fieldForceAccountSelected?.length > GlobalConfiguration.lengthCheckZero
      ) {
        const attemptedRevision = selectionRevision;
        setIsUpdating(true);
        let response;
        const selectedCategoryIds = defaultCategories
          .filter((category) => selectedCategories.includes(category.name))
          .map((category) => category.id);
        try {
          response = await dispatch(
            editFieldForceAccount({
              id: id,
              category_ids: selectedCategoryIds,
            }),
          );
        } finally {
          setIsUpdating(false);
          setLastUpdateAttemptRevision(attemptedRevision);
        }

        if (response?.payload?.status === true) {
          CommonToast({
            text: "Categories unassigned succesfully",
            icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
            variant: "var(--success)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          setActiveStep(GlobalConfiguration.lengthCheckZero);
          setCurrentPage(GlobalConfiguration.lengthCheckOne);

          fetchAccountData(GlobalConfiguration.lengthCheckOne, searchUserText);
        }
      }
    } else {
      navigate("/feature_not_assigned", {
        state: {
          unavailableService: "Admin, FFVAccount, PLogin, FFUAccount",
        },
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "all":
        return "var(--text-primary)";
      case "pending":
        return "var(--pending)";
      case "approved":
        return "var(--awaiting)";
      case "rejected":
      case "blocked":
        return "var(--failed)";
      case "archived":
        return "var(--abandoned)";
      default:
        return "var(--text-primary)";
    }
  };

  const dataArray = [
    {
      stepperText: "Select Account",
      nextText: "Next",
      previousText: "Back",
      previousDisableBgColor: "var(--primary-nav-act)",
      previousEnableBgColor: "var(--primary-nav-act)",
      nextIsEnabled:
        !fieldForceAccountsLoading &&
        Array.isArray(fieldForceAccounts) &&
        fieldForceAccounts.length > GlobalConfiguration.lengthCheckZero &&
        Array.isArray(fieldForceAccountSelected) &&
        fieldForceAccountSelected.length > GlobalConfiguration.lengthCheckZero,
      nextEnableBgColor: "var(--positive-act)",
      nextDisableBgColor: "var(--text-disabled)",
      nextEnableTextColor: "var(--text-primary)",
      nextDisableTextColor: "var(--text-secondary)",
    },
    {
      stepperText: "Select Category",
      nextText: "Update",
      previousText: "Back",
      previousDisableBgColor: "var(--primary-nav-act)",
      previousEnableBgColor: "var(--primary-nav-act)",
      nextIsEnabled:
        !isUpdating &&
        !isLoadingSelectedAccount &&
        !isCurrentSelectionAlreadyAttempted &&
        Array.isArray(selectedCategories) &&
        selectedCategories.length > GlobalConfiguration.lengthCheckZero &&
        !(
          selectedCategories.includes("all") ||
          (categoriesArray?.length > GlobalConfiguration.lengthCheckZero &&
            categoriesArray.every((item) => selectedCategories.includes(item)))
        ) &&
        !fieldForceAccountsLoading,
      nextEnableBgColor: "var(--positive-act)",
      nextDisableBgColor: "var(--text-disabled)",
      nextLoaderNeeded: true,
      nextIsLoading:
        isUpdating || isLoadingSelectedAccount || fieldForceAccountsLoading,
      nextDisableTextColor: "var(--text-primary)",
    },
  ];

  const handleForward = () => {
    if (isUpdating || isLoadingSelectedAccount) return;
    if (activeStep === GlobalConfiguration.lengthCheckZero) {
      handleClickAccountCard(
        fieldForceAccountSelected?.[GlobalConfiguration.lengthCheckZero],
      );
      setActiveStep(GlobalConfiguration.lengthCheckOne);
    } else if (activeStep === GlobalConfiguration.lengthCheckOne) {
      handleUpdateAccount(
        fieldForceAccountSelected?.[GlobalConfiguration.lengthCheckZero],
      );
    }
  };

  const handleBackward = () => {
    if (activeStep === GlobalConfiguration.lengthCheckOne) {
      setActiveStep(GlobalConfiguration.lengthCheckZero);
    } else {
      navigate("/home");
    }
  };

  const handleSelect = (value) => {
    setCurrentPage(GlobalConfiguration.lengthCheckOne);
    setPageSize(value);
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    const handleResize = () => {
      setCheckmobile(window.innerWidth < ScreenSizeConfiguration.mobileScreen);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const lastEligibleAccountIndex = fieldForceAccounts.length - 1;
  const scrollCardHoverIntoView = (row) => {
    if (window.innerWidth > ScreenSizeConfiguration.tabviewStart) return;

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const scrollContainer = accountListRef.current;
        const card = row.querySelector(".standard-card-container");
        const cardBody = card?.querySelector(".standard-card-div");
        const cardHover = card?.querySelector(".standard-card-hover-div");
        if (!scrollContainer || !card || !cardBody || !cardHover) return;

        const scrollRect = scrollContainer.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        const cardStyle = window.getComputedStyle(card);
        const rootStyle = window.getComputedStyle(document.documentElement);
        const fixedBottomHeight =
          (parseFloat(rootStyle.getPropertyValue("--pagination-height")) ||
            GlobalConfiguration.lengthCheckZero) +
          (parseFloat(rootStyle.getPropertyValue("--stepper-moble-height")) ||
            0);
        const cardPadding =
          parseFloat(cardStyle.paddingTop) +
          parseFloat(cardStyle.paddingBottom);
        const expandedHeight =
          cardBody.getBoundingClientRect().height +
          cardHover.scrollHeight +
          cardPadding;
        const opensUpward = row.classList.contains(
          "field-force-unassign-card-row-last",
        );
        const overflow = opensUpward
          ? scrollRect.top - (rowRect.bottom - expandedHeight)
          : rowRect.top +
            expandedHeight -
            (scrollRect.bottom - fixedBottomHeight);

        if (overflow > 0) {
          scrollContainer.scrollBy({
            top: opensUpward ? -overflow : overflow,
            behavior: "smooth",
          });
        }
      });
    });
  };

  if (fieldForceAccountsLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-primary-bg">
        <Loader />
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-screen overflow-hidden`}>
      {!fieldForceAccountsLoading &&
        fieldForceAccounts?.length > GlobalConfiguration?.lengthCheckZero && (
          <div
            className={`${activeStep === GlobalConfiguration?.lengthCheckOne ? "Qsmd:block hidden" : "block"}
                      fixed w-full bottom-bottom-base rounded-none h-pagination-height bg-primary-card ${showInnerSearchBar ? "z-z-index-overlay" : "z-z-index-dropdown"}`}
          >
            {showInnerSearchBar && (
              <div className="absolute bg-PopUp-BackDrop opacity-dark-opacity w-full h-full z-z-index-header pointer-events-none"></div>
            )}
            <PaginationComponent
              handleSelect={handleSelect}
              selectedValue={pageSize}
              data={paginationData}
              pageSize={pageSize}
              dropdownOptions={dropdownOptions}
              handlePagination={handlePagination}
              currentPage={currentPage}
              setIsOpen={(e) => {}}
            />
          </div>
        )}

      {!fieldForceAccountsLoading &&
        fieldForceAccounts?.length > GlobalConfiguration?.lengthCheckZero && (
          <div
            className={`${showInnerSearchBar ? "opacity-lightest-opacity" : ""} ${activeStep === GlobalConfiguration?.lengthCheckOne ? "bottom-bottom-base" : "bottom-unassign-bottom-md"} Qsmd:hidden block fixed w-full bg-primary-card rounded-t-border-radius-md min-h-[calc(var(--day-date-card-height)+var(--dashed-line))] z-z-index-header`}
          >
            <PrimaryStepper
              activeStep={activeStep}
              handleForwardAction={handleForward}
              handleBackwardAction={handleBackward}
              dataArray={dataArray}
            />
          </div>
        )}

      <div
        className={`bg-primary-bg flex flex-col flex-1 h-screen overflow-hidden overflow-x-auto `}
        style={{
          position: "relative",
          overflowY: showInnerSearchBar ? "hidden" : "",
          overflowX: "hidden",
          width: "var(--full-width)",
        }}
      >
        <div
          className={` relative z-z-index-overlay bg-primary-nav ${showInnerSearchBar && " fixed  w-full "}`}
        >
          <div className=" ">
            <div
              className={`hidden Qsmd:block h-navbar-breadcrumb-height   w-full `}
            ></div>
          </div>
        </div>

        <div
          className={`field_force_div_container Qmd:shadow-box-shadow-bottom Qmd:bg-primary-nav bg-primary-nav-act w-full-width relative `}
        >
          <TopNavComponent
            handleBack={() => {
              if (activeStep === GlobalConfiguration.lengthCheckOne) {
                setActiveStep(GlobalConfiguration.lengthCheckZero);
                return;
              }
              navigate(GlobalConfiguration.lengthCheckNegativeOne);
            }}
            topNavCtaBtn2Needed={true}
            topNavCtaBtn2Props={
              checkmobile
                ? undefined
                : {
                    btnText: "Update",
                    btnType: "button",
                    enabledBgColor:
                      isUpdating ||
                      isLoadingSelectedAccount ||
                      isCurrentSelectionAlreadyAttempted ||
                      fieldForceAccountsLoading ||
                      fieldForceAccounts?.length ===
                        GlobalConfiguration.lengthCheckZero ||
                      selectedCategories?.length ===
                        GlobalConfiguration.lengthCheckZero ||
                      categoriesArray.every((item) =>
                        selectedCategories.includes(item),
                      )
                        ? "var(--text-disabled)"
                        : "var(--positive-act)",
                    enabledTextColor: "var(--text-primary)",
                    disabledTextColor: "var(--text-primary)",
                    handleFunction: () => {
                      if (
                        isUpdating ||
                        isLoadingSelectedAccount ||
                        isCurrentSelectionAlreadyAttempted ||
                        fieldForceAccounts?.length ===
                          GlobalConfiguration.lengthCheckZero ||
                        selectedCategories?.length ===
                          GlobalConfiguration.lengthCheckZero ||
                        categoriesArray.every((item) =>
                          selectedCategories.includes(item),
                        )
                      ) {
                        return;
                      }
                      handleUpdateAccount(
                        fieldForceAccountSelected?.[
                          GlobalConfiguration.lengthCheckZero
                        ],
                      );
                    },
                    isDisabled:
                      isUpdating ||
                      isLoadingSelectedAccount ||
                      isCurrentSelectionAlreadyAttempted ||
                      fieldForceAccountsLoading ||
                      fieldForceAccounts?.length ===
                        GlobalConfiguration.lengthCheckZero ||
                      selectedCategories?.length ===
                        GlobalConfiguration.lengthCheckZero ||
                      categoriesArray.every((item) =>
                        selectedCategories.includes(item),
                      ),
                    isLoading: isUpdating || fieldForceAccountsLoading,
                    isLoaderNeeded: true,
                    hoverOption: "white",
                    inActiveVariantNeeded: true,
                  }
            }
            filteredSearchProps={{
              setShowInnerSearchBar: setShowInnerSearchBar,
              isSearchDisabled: fieldForceAccountsLoading,
              onSearchInput: (val) => handleSearch(val),
              searchText: searchUserTextDebounce,
              searchPlaceholder: "Search field force account",
              filterPlaceholder:
                "Search by account ID/ name/contact number/email ID",
              filterItems: [
                {
                  name: "Search",
                  isMulti: false,
                  value: searchUserText,
                  setValue: (val) => {
                    setCurrentPage(GlobalConfiguration.lengthCheckOne);
                    setSearchUserText(val);
                  },
                  isDropdown: false,
                  isDisabled: fieldForceAccountsLoading,
                },
              ],
              filters: [
                {
                  name: "Account status",
                  filterType: "dropdown",
                  variant: "single",
                  size: "small",
                  value: currentStatus,
                  dropdownPlaceHolder: "Select status",
                  isDisable: false,
                  options: ["approved", "pending", "rejected"],
                  statusPop: true,
                  handleChange: (e) => {
                    setCurrentPage(GlobalConfiguration.lengthCheckOne);
                    if (!e || e.toLowerCase() === "all") {
                      setCurrentStatus("");
                    } else {
                      setCurrentStatus(e);
                    }
                  },
                },
              ],
              isFilterClearAllDisabled:
                !(currentStatus !== "all" && currentStatus !== "") ||
                fieldForceAccountsLoading,
              isSearchClearAllDisabled:
                !searchUserText?.length || fieldForceAccountsLoading,
              inputData: inputData,
              isShowFilter: true,
              active: active,
              setActive: setActive,
              loading: dataLoading,
              setLoading: (e) => {},
            }}
          />
        </div>
        <div className="fieldForceTxnViewAll_middle_section_container mx-auto webViewRejectAll"></div>

        {fieldForceAccounts?.length === GlobalConfiguration.lengthCheckZero ? (
          <div className="field_force-no-account-image-div h-full">
            {fieldForceAccountsLoading &&
            fieldForceAccounts?.length ===
              GlobalConfiguration.lengthCheckZero ? (
              <div className="loader-container flex items-center justify-center">
                <Loader />
              </div>
            ) : fieldForceAccounts?.length ===
                GlobalConfiguration.lengthCheckZero &&
              !fieldForceAccountsLoading ? (
              <>
                <div
                  className={
                    showInnerSearchBar
                      ? ` h-screen w-width-full bg-PopUp-BackDrop opacity-light-opacity flex flex-col items-center justify-center m-auto`
                      : "flex flex-col items-center justify-center m-auto overflow-y-scroll"
                  }
                >
                  <img
                    src={ICONS?.NO_DATA_FOUND}
                    alt="No accounts found"
                    className={`w-graphic-width-md h-graphic-rectangle-height-md
              ${showInnerSearchBar ? "opacity-light-opacity" : "opacity-base-opacity"}`}
                  />

                  <p
                    className={
                      showInnerSearchBar
                        ? "no-owneraccount-text-opacity"
                        : "text-text-primary opacity-base-opacity text-font-sm text-center py-padding-sm"
                    }
                  >
                    No accounts found.
                  </p>

                  <div
                    ref={searchActionBtnRef}
                    style={{
                      opacity: showInnerSearchBar
                        ? "var(--light-opacity)"
                        : "var(--base-opacity)",
                      pointerEvents: showInnerSearchBar ? "none" : "auto",
                      display: !showInnerSearchBar ? "flex" : "none",
                    }}
                    className="oas-selectaccount"
                  >
                    <PrimaryButton
                      isDisabled={showInnerSearchBar}
                      enabledBgColor="var(--primary-act)"
                      btnText={
                        searchUserText?.length >
                          GlobalConfiguration.lengthCheckZero ||
                        (currentStatus !== "" && currentStatus !== "all")
                          ? "Clear parameters"
                          : "Home dashboard"
                      }
                      enabledTextColor="var(--text-primary)"
                      handleFunction={(e) => {
                        e.stopPropagation();
                        if (
                          searchUserText?.length >
                            GlobalConfiguration.lengthCheckZero ||
                          (currentStatus !== "" && currentStatus !== "all")
                        ) {
                          setSearchUserText("");
                          setCurrentStatus("");
                          setCurrentPage(GlobalConfiguration.lengthCheckOne);
                        } else {
                          navigate("/home");
                        }
                      }}
                      buttonRef={null}
                      hoverOption="white"
                    />
                  </div>
                </div>
              </>
            ) : null}
          </div>
        ) : (
          <div
            className={`${showInnerSearchBar && "bg-PopUp-BackDrop opacity-dark-opacity h-screen"} h-full overflow-hidden`}
          >
            <div
              className={`flex Qsmd:flex-row flex-col flex-1  ${showInnerSearchBar ? "bg-PopUp-BackDrop opacity-light-opacity overflow-hidden" : "bg-primary-bg overflow-y-hidden"} h-full w-full`}
            >
              <div
                className={`Qsmd:mb-margin-base h-height-full min-w-0 Qsmd:min-w-fit flex justify-end min-h-0 Qsmd:pb-pagination-height
               ${showInnerSearchBar ? "bg-PopUp-BackDrop opacity-light-opacity overflow-hidden" : "bg-primary-card overflow-y-hidden"}
                ${fieldForceAccountsLoading ? "w-full" : "Qsmd:w-width-sm"}
                ${activeStep === GlobalConfiguration.lengthCheckZero ? "block" : "hidden"} 
                ${showInnerSearchBar && "pointer-events-none"}
                `}
              >
                {fieldForceAccountsLoading ? (
                  <div className="loader-container flex Qsmd:justify-normal justify-center items-center h-full Qsmd:w-width-md w-full">
                    <Loader />
                  </div>
                ) : (
                  <>
                    <div
                      ref={accountListRef}
                      className={`pb-[calc(var(--pagination-height)_+_var(--stepper-moble-height))] Qsmd:pb-padding-sm !flex !flex-col !overflow-auto h-height-full min-h-0 overflow-x-hidden flex-1 min-w-0 Qsmd:min-w-fit overflow-y-auto ${showInnerSearchBar ? "pointer-events-none" : ""}`}
                    >
                      {/* <div className="items-end flex flex-col max-w-full gap-x-gap-sm"> */}
                      {fieldForceAccounts?.map((val, index) => (
                        <div
                          key={val?.id}
                          className={`field-force-unassign-card-row ${
                            index === lastEligibleAccountIndex
                              ? "field-force-unassign-card-row-last"
                              : ""
                          } flex gap-gap-md py-padding-sm px-padding-md relative justify-end w-full min-w-0 Qsmd:min-w-[var(--card-max-width)]`}
                          onMouseEnter={(event) =>
                            scrollCardHoverIntoView(event.currentTarget)
                          }
                          onTouchStart={(event) =>
                            scrollCardHoverIntoView(event.currentTarget)
                          }
                        >
                          <div className="acc-data-sep-line mt-[calc(-1*var(--margin-md))]">
                            <div
                              className={`acc-data-div-sep-line_field_force
                                    ${
                                      index ===
                                      GlobalConfiguration.lengthCheckZero
                                        ? "h-sep-line_field_force_unassign_cat_half"
                                        : "h-sep-line_field_force_unassign_cat_full"
                                    }
                                    ${
                                      index ===
                                      GlobalConfiguration.lengthCheckZero
                                        ? ""
                                        : "mt-[var(--reimbursment-cat-zeroth-item)]"
                                    }
                                    `}
                            ></div>
                            <div
                              className="acc-data-div-sep-line-dot_field_force absolute max-w-standard-card-width"
                              style={{
                                width: fieldForceAccountSelected.includes(
                                  val?.id,
                                )
                                  ? "var(--icon-md)"
                                  : "var(--icon-sm)",
                                height: fieldForceAccountSelected.includes(
                                  val?.id,
                                )
                                  ? "var(--icon-sm)"
                                  : "var(--icon-sm)",
                                borderRadius:
                                  fieldForceAccountSelected.includes(val?.id)
                                    ? "var(--margin-button-m)"
                                    : "var(--width-md)",
                              }}
                            >
                              <div
                                className="acc-data-div-sep-line-inner-dot_field_force"
                                style={{
                                  width: fieldForceAccountSelected.includes(
                                    val?.id,
                                  )
                                    ? "var(--icon-sm)"
                                    : "var(--margin-button-m)",
                                  height: fieldForceAccountSelected.includes(
                                    val?.id,
                                  )
                                    ? "var(--margin-button-m)"
                                    : "var(--margin-button-m)",
                                  borderRadius:
                                    fieldForceAccountSelected.includes(val?.id)
                                      ? "var(--margin-button-m)"
                                      : "var(--width-md)",
                                }}
                              ></div>
                            </div>
                          </div>

                          <CardContainer
                            key={index}
                            // maxWidth="var(--card-max-width)"
                            context={val}
                            index={index}
                            cardBgColor="primary-nav"
                            isSelected={
                              Array.isArray(fieldForceAccountSelected) &&
                              fieldForceAccountSelected.includes(val.id)
                            }
                            isDisabled={showInnerSearchBar ? true : false}
                            onClickHandler={() => {
                              if (!showInnerSearchBar) {
                                handleClickAccountCard(val?.id);
                              }
                            }}
                          >
                            <card-body>
                              <TextWithIcon
                                type="text"
                                fontWeight="normal"
                                subText="Account name"
                                text={val.name
                                  ?.toLowerCase()
                                  .split(" ")
                                  .map(
                                    (word) =>
                                      word
                                        .charAt(
                                          GlobalConfiguration.lengthCheckZero,
                                        )
                                        .toUpperCase() +
                                      word.slice(
                                        GlobalConfiguration.lengthCheckOne,
                                      ),
                                  )
                                  .join(" ")}
                                textColor={"var(--text-primary)"}
                              />

                              <div className="flex gap-x-gap-sm justify-between items-center">
                                <div className="flex flex-1 truncate flex-col gap-y-gap-xs">
                                  <p
                                    className={`text-font-sm text-text-primary flex-1 truncate font-normal pl-mobile-padding-l Qmd:pl-padding-sm`}
                                  >
                                    <TickerAnimation
                                      text={formatAccountNumber(
                                        val.bank_acc_no,
                                      ).toUpperCase()}
                                    />
                                  </p>
                                  <div className="w-full relative flex pl-mobile-padding-l Qmd:pl-padding-sm justify-between items-center">
                                    <p
                                      className={`text-font-xs text-text-secondary flex-1 truncate font-normal transform-none`}
                                    >
                                      Account number
                                    </p>
                                  </div>
                                </div>
                                <div className="bg-text-primary flex items-center gap-x-gap-sm rounded-l-border-radius-md py-padding-xs px-padding-sm">
                                  <div className="w-banklogo-width-sm h-icon-xs flex-shrink-0">
                                    <IfscCodeMapper ifscCode={val?.ifsc_code} />
                                  </div>
                                </div>
                              </div>
                            </card-body>
                            <card-hover>
                              <TextWithIcon
                                text={val?.approval_status}
                                subText={"Account status"}
                                textCase={"capitalize"}
                                fontWeight={"semibold"}
                                textColor={getStatusColor(val?.approval_status)}
                              />
                              <TextWithIcon
                                text={
                                  val?.labels?.join(", ") ||
                                  "No labels assigned"
                                }
                                iconComponentArray={[
                                  {
                                    icon: ICONS.LABEL,
                                  },
                                ]}
                                fontWeight={"normal"}
                                textColor={"var(--text-primary)"}
                              />
                            </card-hover>
                          </CardContainer>
                        </div>
                      ))}
                      {/* </div> */}
                    </div>
                  </>
                )}
              </div>
              {!fieldForceAccountsLoading && (
                <div
                  className={`flex flex-1 justify-start no-scrollbar w-full Qsmd:w-width-lg
                ${activeStep === GlobalConfiguration.lengthCheckOne ? "block" : "hidden"}
                Qsmd:flex
                ${showInnerSearchBar ? "overflow-hidden pointer-events-none bg-PopUp-BackDrop opacity-light-opacity" : "overflow-y-auto   bg-primary-bg"}
              `}
                >
                  <div className="p-padding-md w-full">
                    <div className="Qsmd:max-w-[var(--create-box-width)] ">
                      <CategoriesEdit
                        key={
                          fieldForceAccountSelected?.[
                            GlobalConfiguration.lengthCheckZero
                          ] || "categories-edit-key"
                        }
                        sortedCategories={sortedCategories}
                        selectedCategories={selectedCategoryIds}
                        handleCategorySelect={handleCategorySelect}
                        Title="Assign Categories"
                        SubTitle="Not selecting categories will assign all, including future ones, to this account. This is not recommended for security reasons."
                        TitleFontcolor="text-primary"
                        TextSize="font-md"
                        SubTitleFontcolor="text-secondary"
                        SubTitleTextSize="font-xs"
                        TitleFontWeight="semibold"
                        SubTitleFontWeight="normal"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {!fieldForceAccountsLoading &&
        fieldForceAccounts?.length > GlobalConfiguration?.lengthCheckZero && (
          <div
            className={`flex flex-shrink-0 bg-primary-card ${activeStep === GlobalConfiguration?.lengthCheckOne ? "h-pagination-height" : "h-[var(--graphic-width-sm)] Qsmd:h-pagination-height"}`}
          ></div>
        )}
    </div>
  );
};

export default FieldForceUnassignCategories;
